import { createContext, useContext, useState, useCallback, useMemo, useEffect, type ReactNode, type ReactElement } from 'react';
import { Snackbar, Alert, Slide } from '@mui/material';
import { AlertMessages } from '../config/data/Constants';
import { logEvent } from '../config/data/firebase';

interface AlertItem {
	message: string;
	type: 'success' | 'error' | 'warning' | 'info';
}

const firebaseErrorMessages: Record<string, string> = {
	'auth/email-already-in-use': 'This email is already registered. Please log in.',
	'auth/weak-password': 'Your password is too weak. Please use a stronger password.',
	'auth/invalid-email': 'Invalid email address format.',
	'auth/user-disabled': 'This account has been disabled by an administrator.',
	'auth/user-not-found': 'No account found with this email.',
	'auth/wrong-password': 'Incorrect password. Please try again.',
	'auth/network-request-failed': 'Network error. Check your connection and try again.',
	'auth/password-does-not-meet-requirements':
		'Your password must contain at least 8 characters, a lower case character, an upper case character, and a non-alphanumeric character.',
};

export interface AlertContextValue {
	showAnnouncement: (params: { message?: string }) => ReactElement | null;
	showAlert: (categoryOrCustom: string | { message: string; type?: string }, type?: string | null) => void;
	handleError: (error: unknown, context?: string, show?: boolean | null) => void;
}

const AlertContext = createContext<AlertContextValue | undefined>(undefined);

export const AlertProvider = ({ children }: { children: ReactNode }) => {
	const [alertQueue, setAlertQueue] = useState<AlertItem[]>([]);
	const [currentAlert, setCurrentAlert] = useState<AlertItem | null>(null);

	const showAnnouncement = useCallback(({ message }: { message?: string }) => {
		if (!message) {
			return null;
		}
		return (
			<Alert severity='info' sx={{ width: '100%', mt: 1, boxSizing: 'border-box' }}>
				{message}
			</Alert>
		);
	}, []);

	const showAlert = useCallback(
		(categoryOrCustom: string | { message: string; type?: string }, type: string | null = null) => {
			let newAlert: AlertItem;

			if (typeof categoryOrCustom === 'string' && type) {
				newAlert = AlertMessages[categoryOrCustom]?.[type] || { message: 'Unknown alert', type: 'info' };
			} else if (typeof categoryOrCustom === 'object' && categoryOrCustom.message) {
				newAlert = { message: categoryOrCustom.message, type: (categoryOrCustom.type || 'info') as AlertItem['type'] };
			} else {
				console.error('❌ Invalid alert parameters.');
				return;
			}

			setAlertQueue((prevQueue) => {
				const lastAlert = prevQueue[prevQueue.length - 1] || currentAlert;
				if (lastAlert && lastAlert.message === newAlert.message && lastAlert.type === newAlert.type) {
					return prevQueue;
				}
				return [...prevQueue, newAlert];
			});
		},
		[currentAlert]
	);

	useEffect(() => {
		if (!currentAlert && alertQueue.length > 0) {
			setCurrentAlert(alertQueue[0]);
			setAlertQueue((prevQueue) => prevQueue.slice(1));
		}
	}, [alertQueue, currentAlert]);

	const closeAlert = useCallback(() => {
		setCurrentAlert(null);
	}, []);

	const handleError = useCallback(
		(error: unknown, context = 'general', show: boolean | null = null) => {
			const override = show === null ? process.env.REACT_APP_environment !== 'production' : show;
			const errorCode = (error as { code?: string })?.code || '';
			const userFriendlyMessage =
				firebaseErrorMessages[errorCode] || (error as { message?: string })?.message || 'An unexpected error occurred.';

			if (override) {
				showAlert({ message: userFriendlyMessage, type: 'error' });
			}

			logEvent(context, error);
		},
		[showAlert]
	);

	const contextValue = useMemo<AlertContextValue>(
		() => ({ showAnnouncement, showAlert, handleError }),
		[showAnnouncement, showAlert, handleError]
	);

	return (
		<AlertContext.Provider value={contextValue}>
			{children}
			{currentAlert && (
				<Snackbar
					open
					autoHideDuration={5000}
					onClose={closeAlert}
					anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
					slots={{
						transition: Slide,
					}}
				>
					<Alert
						onClose={closeAlert}
						severity={currentAlert.type}
						variant='filled'
						sx={{
							fontSize: '1.2rem',
							fontWeight: 'bold',
							padding: '1rem',
							borderRadius: '10px',
							textAlign: 'center',
							boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
						}}
					>
						{currentAlert.message}
					</Alert>
				</Snackbar>
			)}
		</AlertContext.Provider>
	);
};

export const useAlert = (): AlertContextValue => {
	const context = useContext(AlertContext);
	if (context === undefined) {
		throw new Error('useAlert must be used within an AlertProvider');
	}
	return context;
};
